// New version src/Common/locale/locale.service.ts CommonLocaleService
// Old version src/CaseDotStar.ServicePackages.Frontend.Locale/scripts/locale/locale_run.js LocaleRun

import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { dayjs } from '../dayjs-config';

import { CommonUserDataService } from '../user/user_data.service';
import { CommonGeneralSettingsService } from '../general_settings/general_settings.service';
import { BehaviorSubject } from 'rxjs';

export interface ICommonLocaleServiceData {
	localeKey: string,
	localeUrl?: string,
	translations?: {[key: string]: string},
	layoutTranslations?: {  // translations in layout for initial loader and bootstrap error
		Layout_Loading: string,
		Layout_Reconnect_StartMessage: string,
		Layout_Reconnect_Seconds: string,
		Layout_RunError: string,
		Layout_Error: string,
		Layout_Error_Message: string,
		Layout_ServerResponse: string,
	},
}

@Injectable({ providedIn: 'root' })
export class CommonLocaleService {
	readonly locale: string = window.LocaleData.localeKey;
	localeData: ICommonLocaleServiceData;

	private init$ = new BehaviorSubject<boolean>(false);

	constructor (
		private translateService: TranslateService,
		private commonUserDataService: CommonUserDataService,
		private commonGeneralSettingsService: CommonGeneralSettingsService,
	) {}

	init (): Promise<boolean> {
		return new Promise((resolve) => {  // for APP_INITIALIZER
			this.localeData = window.LocaleData;
			this.translateService.setDefaultLang(this.locale);
			const observable = this.translateService.use(this.locale);

			observable.subscribe(() => {
				this.init$.next(true);
				resolve(true);
			});

			this.commonGeneralSettingsService.wait().subscribe((result) => {
				if (result) {
					this.setFormats();
				}
			});

			this.commonUserDataService.waitData().subscribe(() => {
				this.setTimeZone();
			});
		});
	}

	setFormats (): void {
		const timeFormat = this.commonGeneralSettingsService.getGeneralSettings('TimeFormat');
		const dateFormat = this.commonGeneralSettingsService.getGeneralSettings('DateFormat');
		const locale = this.locale === 'en' ? 'en-gb' : this.locale;

		if (timeFormat) {
			const dayOfWeek = this.commonGeneralSettingsService.getGeneralSettings('DayOfWeek');
			const weekStartValue = (dayOfWeek.IndexNumber + 1) % 7;

			const weekdaysAccusative = [
				'воскресенье',
				'понедельник',
				'вторник',
				'среду',
				'четверг',
				'пятницу',
				'субботу'
			];

			const weekdaysLastWeek = [
				'прошлое воскресенье',
				'прошлый понедельник',
				'прошлый вторник',
				'прошлую среду',
				'прошлый четверг',
				'прошлую пятницу',
				'прошлую субботу'
			];

			const calendarFormats = {
				ru: {
					lastDay: '[Вчера, в] LT',
					sameDay: 'LT',
					nextDay: '[Завтра, в] LT',
					lastWeek () {
						const weekday = this.day();
						const now = dayjs();
						const startOfThisWeek = now.startOf('week');
						const prefix = weekday === 2 ? 'Во ' : 'В ';

						if (this.isBefore(startOfThisWeek)) {
							return prefix + weekdaysLastWeek[weekday] + this.format('[, в] LT');
						}

						return prefix + weekdaysAccusative[weekday] + this.format('[, в] LT');
					},
					nextWeek () {
						const weekday = this.day();
						const prefix = weekday === 2 ? 'Во ' : 'В ';

						return prefix + weekdaysAccusative[weekday] + this.format('[, в] LT');
					},
					sameElse: 'L'
				},
				'en-gb': {
					lastDay: '[Yesterday at] LT',
					sameDay: 'LT',
					nextDay: '[Tomorrow at] LT',
					lastWeek: '[Last] dddd [at] LT',
					nextWeek: 'dddd [at] LT',
					sameElse: 'L'
				},
				fr: {
					lastDay: '[Hier à] LT',
					sameDay: 'LT',
					nextDay: '[Demain à] LT',
					lastWeek: 'dddd [dernier à] LT',
					nextWeek: 'dddd [à] LT',
					sameElse: 'L'
				},
			pt: {
				lastDay: '[Ontem às] LT',
				sameDay: 'LT',
				nextDay: '[Amanhã às] LT',
				lastWeek() {
					const weekday = this.day();
					const isMasculine = weekday === 0 || weekday === 6; // domingo, sábado
					const prefix = isMasculine ? 'Último ' : 'Última ';
					const weekdayName = this.format('dddd');
					const capitalizedWeekday = weekdayName.charAt(0).toUpperCase() + weekdayName.slice(1);

					return prefix + capitalizedWeekday + this.format(' [às] LT');
				},
				nextWeek: 'dddd [às] LT',
				sameElse: 'L'
			}
			};

			const updateConfig: any = {
				formats: {
					LT: timeFormat.Format,
					L: dateFormat.ShortFormat
				},
				meridiem: (hours: number, minutes?: number) => {
					const isPM = hours > 12 || (hours === 12 && (minutes ?? 0) > 0);

					return isPM ? timeFormat.PmDesignator : timeFormat.AmDesignator;
				},
				weekStart: weekStartValue,
				calendar: calendarFormats[locale] || calendarFormats['en-gb']
			};

			if (this.locale === 'ru') {
				updateConfig.weekdaysShort = 'вс_пн_вт_ср_чт_пт_сб'.split('_');
			}

			dayjs.locale(locale);
			dayjs.updateLocale(locale, updateConfig);
		}
	}

	setTimeZone() {
		const userTimeZone = this.commonUserDataService.getDataByPath('TimeZone');

		if (userTimeZone && userTimeZone.IanaId) {
			dayjs.tz.setDefault(userTimeZone.IanaId);
		}
	}

	instant (key: string, params?: {}): string {
		return this.translateService.instant(key, params);
	}

	wait(): BehaviorSubject<boolean> {
		return this.init$;
	}
}
